import type { ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Box, Button } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { StateMessage } from "@gui/widgets/AsyncState/StateMessage";
import { useResetOnNavigate, useSectionErrorBoundaryViewModel } from "./SectionErrorBoundary.vm";

interface SectionErrorBoundaryProps {
	/** Names the area in Sentry (the `ui.section` tag), e.g. "job-map". */
	section: string;
	/** Clear the fallback when the route changes. Needs a router above it. */
	resetOnNavigate?: boolean;
	children: ReactNode;
}

/**
 * Contains a render crash to one area of the page. Without it, an error in a map or table
 * reaches the root boundary in `main.tsx` and replaces the whole portal, navigation included.
 * The crash is still reported to Sentry, tagged with the section, and the fallback lets the
 * user retry the area or describe what happened through the feedback widget.
 */
export function SectionErrorBoundary({ section, resetOnNavigate, children }: SectionErrorBoundaryProps) {
	const vm = useSectionErrorBoundaryViewModel(section);
	return (
		<Sentry.ErrorBoundary
			beforeCapture={vm.tagSection}
			fallback={({ eventId, resetError }) => (
				<>
					{resetOnNavigate && <ResetOnNavigate resetError={resetError} />}
					<StateMessage
						role="alert"
						glyph={<ErrorOutline color="error" fontSize="large" />}
						title="This part of the page stopped working"
						detail="The software team has been notified. The rest of the portal still works."
						action={
							<Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
								<Button variant="outlined" size="small" onClick={resetError}>
									Try again
								</Button>
								<Button size="small" onClick={() => vm.reportProblem(eventId)}>
									Report problem
								</Button>
							</Box>
						}
					/>
				</>
			)}
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}

function ResetOnNavigate({ resetError }: { resetError: () => void }) {
	useResetOnNavigate(resetError);
	return null;
}
